import isEqual from 'lodash.isequal';
import { option as fptsOption } from 'fp-ts';
import intersection from 'lodash.intersection';
import { fromEmpty } from '../../helpers/typeclasses';

export type $ConfigurationState = Object;

const initialState = {
  hoveredOption: null,
};

/* Constants */
const SET_PRICE_CONFIG = 'CONFIGURATION/SET_PRICE_CONFIG';
const SET_CURRENT_PRICE_CONFIG = 'CONFIGURATION/SET_CURRENT_PRICE_CONFIG';
const SET_AVAILABLE_PRODUCTS = 'CONFIGURATION/SET_AVAILABLE_PRODUCTS';
const SET_HOVERED_OPTION = 'CONFIGURATION/SET_HOVERED_OPTION';
const UNSET_HOVERED_OPTION = 'CONFIGURATION/UNSET_HOVERED_OPTION';

/* Actions */
const setPriceConfig = (priceConfig: Object) => ({
  type: SET_PRICE_CONFIG,
  payload: {
    prices: priceConfig,
  },
});

export const updatePrice = () => (dispatch: Function, getState: Function) => {
  const { configuration, attributes } = getState();
  const { prices, optionPrices } = configuration;
  const statePrices = fptsOption.fromNullable(prices);
  statePrices
    .map((p) => {
      const { index: productIndex } = configuration;
      if (!productIndex || !productIndex.length) {
        return null;
      }
      const firstProductConfig = Object.keys(productIndex)[0];
      const attributeKeys = Object.keys(productIndex[firstProductConfig]);
      const productConfig = attributeKeys.reduce((config, attributeKey) => {
        const attribute = attributes.filter(attr => attr.id === attributeKey)[0];
        return {
          ...config,
          ...(attribute.value ? { [attribute.id]: attribute.value } : {}),
        };
      }, {});
      if (Object.keys(attributeKeys).length === Object.keys(productConfig).length) {
        return Object.keys(productIndex).filter(key => isEqual(productIndex[key], productConfig))[0];
      }
      return false;
    })
    .chain(priceConfig => fptsOption.fromNullable(priceConfig)
      .map(() => optionPrices[priceConfig])
      .map(p => dispatch(setPriceConfig(p)))).getOrElse(() => {});
};

const setAvailableProductsAction = availableProducts => ({
  type: SET_AVAILABLE_PRODUCTS,
  payload: {
    availableProducts,
  },
});

const getHoverImageUrl = imgUrl => imgUrl.replace('.jpg', '_1x.jpg');

const createHoveredOption = (opt, offsetTop) => ({
  label: opt.hover_label,
  description: opt.hover_description,
  price: opt.hover_price,
  image: getHoverImageUrl(opt.hover_image),
  offsetTop,
});

export const setHoveredOption = (opt, offsetTop) => (dispatch) => {
  if (opt.hover_label === '' && opt.hover_image === '') {
    return;
  }
  dispatch({
    type: SET_HOVERED_OPTION,
    payload: {
      option: createHoveredOption(opt, offsetTop),
    },
  });
};

export const unsetHoveredOption = () => ({
  type: UNSET_HOVERED_OPTION,
});

/**
 * calculateAvailableProducts returns an array of product ids that is determined
 * by the intersection of attribute options' products available.
 *
 * For example, given attribute A of which the user has chosen value X, X provides
 * Y (products available for that value). For all attributes NOT including X, we
 * determine the attribute values that intersect with the products available (Y)
 * provided by X.
 * @param {*} attributes
 * @return [number]
 */
export const calculateAvailableProducts = attributes => (skipAttributeId) => {
  const products = attributes
    .filter(attr => attr.id !== skipAttributeId && attr.value)
    .map(attr => fptsOption
      .fromNullable(attr.options.filter(opt => opt.id === attr.value)[0].products)
      .alt(fptsOption.fromNullable(attr.products))
      .chain(fromEmpty)
      .getOrElseValue([]),
    ).filter(arr => arr.length > 0);
  return intersection(...products);
};

export const getPriceList = attributes => (configuration) => {
  const products = attributes
    .filter(attr => attr.value && !attr.hidden)
    .map(attr => fptsOption
      .fromNullable(attr.options.filter(opt => opt.id === attr.value)[0].products)
      .getOrElseValue([]))
    .filter(arr => arr.length > 0);
  const priceList =
  intersection(...products)
    .map(item => configuration.optionPrices[item])
    .filter((obj, pos, arr) => arr.map(mapObj => mapObj.finalPrice.amount).indexOf(obj.finalPrice.amount) === pos);
  return priceList;
};

/**
 * setCurrentPriceConfig updates the price data for the currently selected configuration
 * @param {*} priceConfig
 */
export const setCurrentPriceConfig = priceConfig => ({
  type: SET_CURRENT_PRICE_CONFIG,
  payload: {
    priceConfig,
  },
});


/**
 * setAvailableProducts returns an array of products that are available given the current user-selected configuration.
 *
 * This works by iterating over all attributes and calculating the intersection of available products based on
 * the selected option of each attribute.
 */
export const setAvailableProducts = () => (dispatch: Function, getState: Function) => {
  const { attributes, configuration } = getState();
  const productsCalculator = calculateAvailableProducts(attributes);
  const availableProducts = attributes.length > 1 ?
    attributes.reduce((available, attr) => ({
      ...available,
      [attr.id]: productsCalculator(attr.id),
    }), {})
    : productsCalculator(null);

  const priceConfig = getPriceList(attributes)(configuration)[0];
  dispatch(setCurrentPriceConfig(priceConfig));

  dispatch(setAvailableProductsAction(availableProducts));
};

/* Reducer */
export default function reducer(state: $ConfigurationState = initialState, action: Object) {
  switch (action.type) {
    case SET_PRICE_CONFIG: {
      const { prices } = action.payload;
      return {
        ...state,
        prices,
      };
    }

    case SET_CURRENT_PRICE_CONFIG: {
      const { priceConfig } = action.payload;
      return {
        ...state,
        prices: {
          ...state.prices,
          ...priceConfig,
        },
      };
    }

    case SET_AVAILABLE_PRODUCTS: {
      const { availableProducts } = action.payload;
      return {
        ...state,
        availableProducts,
      };
    }

    case SET_HOVERED_OPTION: {
      const { option } = action.payload;
      return {
        ...state,
        hoveredOption: option,
      };
    }

    case UNSET_HOVERED_OPTION: {
      return {
        ...state,
        hoveredOption: null,
      };
    }

    default: {
      return state;
    }
  }
}

/* Selectors */

export const hoveredOptionSelector = state => state.configuration && state.configuration.hoveredOption


// WEBPACK FOOTER //
// ./src/store/modules/configuration.js